<template>
  <div class="create-api-key-modal">
    <template v-if="!createdRawKey">
      <p class="create-api-key-modal__help">
        {{ $t('settings.api_keys.create_modal_help') }}
      </p>
      <TextInput
        id="create-api-key-name"
        :label="$t('settings.api_keys.name_label')"
        v-model="name"
        :placeholder="$t('settings.api_keys.name_placeholder')"
        @keyup.enter="handleCreate"
      />
      <div class="create-api-key-modal__actions">
        <Btn
          :text="$t('settings.api_keys.cancel')"
          variant="secondary"
          @click="close"
        />
        <Btn
          :text="$t('settings.api_keys.create')"
          variant="primary"
          :loading="creating"
          :disabled="!name.trim()"
          @click="handleCreate"
        />
      </div>
    </template>

    <template v-else>
      <p class="create-api-key-modal__help">
        {{ $t('settings.api_keys.copy_once_help') }}
      </p>
      <div class="create-api-key-modal__secret">
        <code class="create-api-key-modal__secret-value">
          {{ secretVisible ? createdRawKey : maskedSecret }}
        </code>
        <div class="create-api-key-modal__secret-actions">
          <button
            type="button"
            class="create-api-key-modal__icon-btn"
            :title="$t('settings.api_keys.toggle_visibility')"
            @click="secretVisible = !secretVisible"
          >
            <BaseIcon name="eye" :size="16" />
          </button>
          <button
            type="button"
            class="create-api-key-modal__icon-btn"
            :title="$t('settings.api_keys.copy')"
            @click="copySecret"
          >
            <BaseIcon name="copy" :size="16" />
          </button>
        </div>
      </div>
      <div class="create-api-key-modal__actions">
        <Btn
          :text="$t('settings.api_keys.done')"
          variant="primary"
          @click="finish"
        />
      </div>
    </template>
  </div>
</template>

<script>
  import { ref, computed, getCurrentInstance } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import OrganizationService from '@/services/organizationService';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';

  export default {
    name: 'CreateApiKeyModal',
    components: {
      Btn,
      TextInput,
      BaseIcon,
    },
    props: {
      organizationId: {
        type: String,
        required: true,
      },
      modalId: {
        type: [String, Number],
        required: true,
      },
      onCreated: {
        type: Function,
        default: null,
      },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const toastStore = useToastStore();
      const uiStore = useUIStore();
      const organizationService = new OrganizationService();

      const name = ref('');
      const creating = ref(false);
      const createdRawKey = ref('');
      const secretVisible = ref(true);

      const maskedSecret = computed(() => {
        const raw = createdRawKey.value || '';
        if (raw.length <= 8) return '•'.repeat(Math.max(raw.length, 12));
        return `${raw.slice(0, 8)}${'•'.repeat(24)}`;
      });

      const close = () => {
        uiStore.closeModal(props.modalId);
      };

      const handleCreate = async () => {
        const trimmed = name.value.trim();
        if (!trimmed || creating.value) return;

        creating.value = true;
        try {
          const result = await organizationService.createApiKey(props.organizationId, trimmed);
          createdRawKey.value = result.raw_key || '';
          secretVisible.value = true;
          toastStore.addToast({
            type: 'success',
            message: $t('settings.api_keys.create_success'),
          });
          if (props.onCreated) props.onCreated();
        } catch (error) {
          console.error('Failed to create API key:', error);
          toastStore.addToast({
            type: 'error',
            message: error.response?.data?.error || $t('settings.api_keys.create_failed'),
          });
        } finally {
          creating.value = false;
        }
      };

      const copySecret = async () => {
        if (!createdRawKey.value) return;
        try {
          await navigator.clipboard.writeText(createdRawKey.value);
          toastStore.addToast({
            type: 'success',
            message: $t('settings.api_keys.copied_clipboard'),
          });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('settings.api_keys.copy_failed'),
          });
        }
      };

      const finish = () => {
        close();
      };

      return {
        name,
        creating,
        createdRawKey,
        secretVisible,
        maskedSecret,
        close,
        finish,
        handleCreate,
        copySecret,
      };
    },
  };
</script>

<style scoped lang="scss">
  .create-api-key-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__help {
      margin: 0;
      font-size: 0.875rem;
      color: var(--slate-500);
      line-height: 1.45;
    }

    &__secret {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: var(--slate-50);
    }

    &__secret-value {
      flex: 1;
      min-width: 0;
      font-family: 'SF Mono', 'Fira Code', Menlo, Consolas, monospace;
      font-size: 0.8125rem;
      color: var(--slate-800);
      word-break: break-all;
    }

    &__secret-actions {
      display: flex;
      gap: 0.25rem;
      flex-shrink: 0;
    }

    &__icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--slate-500);
      cursor: pointer;

      @include hover {
        background: var(--slate-200);
        color: var(--slate-700);
      }
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      margin-top: 0.25rem;
    }
  }
</style>
